import { useState, useCallback } from 'react'
import { UploadState } from '../types/loan.types'

interface UseFileUploadOptions {
  onSuccess?: (file: File) => void
  onError?: (error: string) => void
  simulateDelay?: number
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const { onSuccess, onError, simulateDelay = 1500 } = options

  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    error: null,
  })

  const simulateUpload = useCallback(
    async (file: File): Promise<void> => {
      setUploadState({ isUploading: true, progress: 0, error: null })

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadState((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 20, 90),
        }))
      }, simulateDelay / 5)

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          clearInterval(progressInterval)

          // Simulate occasional errors (10% chance)
          if (Math.random() < 0.1) {
            const error = 'Upload failed. Please try again.'
            setUploadState({ isUploading: false, progress: 0, error })
            onError?.(error)
            reject(new Error(error))
          } else {
            setUploadState({ isUploading: false, progress: 100, error: null })
            onSuccess?.(file)
            resolve()
          }
        }, simulateDelay)
      })
    },
    [onSuccess, onError, simulateDelay],
  )

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        await simulateUpload(file)
      } catch (error) {
        // Error already handled in simulateUpload
      }
    },
    [simulateUpload],
  )

  const resetState = useCallback(() => {
    setUploadState({ isUploading: false, progress: 0, error: null })
  }, [])

  return {
    ...uploadState,
    uploadFile,
    resetState,
  }
}
